/**
 * MCP Skills 搜索结果组件（完全独立）
 *
 * 与工具搜索结果完全分离：
 * - 独立的组件
 * - 独立的逻辑
 * - 独立的样式
 * - 不影响工具搜索结果功能
 */

import React, { useState } from 'react';
import { Search, AlertCircle, Sparkles, TrendingUp } from 'lucide-react';
import SkillCard from './SkillCard';
import { SkillSearchResult } from '../api/skillApi';

interface SkillSearchResultsProps {
  result: SkillSearchResult | null;
  query: string;
  lang?: 'zh' | 'en';
  loading?: boolean;
  onSkillClick?: (skillName: string) => void;
  useLLM?: boolean;
  onToggleLLM?: () => void;
}

export default function SkillSearchResults({ result, query, lang = 'zh', loading, onSkillClick, useLLM = false, onToggleLLM }: SkillSearchResultsProps) {

  // 加载状态
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* 标题 */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            <Sparkles className="inline w-6 h-6 mr-2 text-purple-500" />
            {lang === 'zh' ? 'MCP Skills 搜索中...' : 'Searching MCP Skills...'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            "{query}"
          </p>
        </div>

        {/* 骨架屏 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-3 w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-full"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-2/3"></div>
              <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded mt-4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 错误状态
  if (!result || result.error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
              {lang === 'zh' ? '搜索失败' : 'Search Failed'}
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {result?.error || (lang === 'zh' ? '无法获取搜索结果，请稍后重试' : 'Unable to get search results, please try again later')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 无结果
  if (result.results.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-12 text-center">
          <Search className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {lang === 'zh' ? '未找到相关 Skills' : 'No Skills Found'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {lang === 'zh'
              ? `没有找到与 "${query}" 相关的 MCP Skills`
              : `No MCP Skills found matching "${query}"`}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {lang === 'zh' ? '建议：尝试使用更通用的关键词，如 "file"、"database" 等' : 'Tip: Try more general keywords like "file", "database", etc.'}
          </p>
        </div>
      </div>
    );
  }

  // 有结果
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* 头部信息 */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              <Sparkles className="inline w-6 h-6 mr-2 text-purple-500" />
              {lang === 'zh' ? 'MCP Skills 搜索结果' : 'MCP Skills Search Results'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              "{query}" • {result.resultCount} {lang === 'zh' ? '个结果' : 'results'} • {result.searchTime}
            </p>
          </div>

          {/* LLM 切换按钮 */}
          {onToggleLLM && (
            <button
              onClick={onToggleLLM}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                useLLM
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
            {useLLM ? (
              <>
                <Sparkles className="inline w-4 h-4 mr-1" />
                {lang === 'zh' ? 'AI 搜索' : 'AI Search'}
              </>
            ) : (
              <>
                <TrendingUp className="inline w-4 h-4 mr-1" />
                {lang === 'zh' ? '快速搜索' : 'Fast Search'}
              </>
            )}
          </button>
          )}
        </div>

        {/* 搜索意图标签 */}
        {result.searchIntent && (
          <div className="mt-3">
            <span className="inline-block px-3 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-sm">
              {result.searchIntent}
            </span>
          </div>
        )}
      </div>

      {/* Skills 卡片网格 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {result.results.map((skill, index) => (
          <SkillCard
            key={skill.id}
            skill={skill}
            rank={index + 1}
            lang={lang}
            onClick={() => onSkillClick && onSkillClick(skill.name)}
          />
        ))}
      </div>

      {/* 相关 Skills */}
      {result.relatedSkills && result.relatedSkills.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
            {lang === 'zh' ? '相关 Skills' : 'Related Skills'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {result.relatedSkills.map((related, index) => (
              <div
                key={index}
                onClick={() => onSkillClick && onSkillClick(related.name)}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors cursor-pointer hover:shadow-md"
              >
                <div className="font-medium text-gray-800 dark:text-gray-100 mb-1">
                  {related.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {related.category}
                </div>
                <div className="text-xs text-purple-600 dark:text-purple-400">
                  {related.reason}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
